import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Plus, Save, Trash2, X, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "../api";
import { useAuth } from "../auth";
import PriceAdjustModal from "./PriceAdjustModal";
import { PriceHistory } from "./PriceHistory";

const CURS = ["TRY", "USD", "EUR"];
const linked = (w, payments) => payments.filter((p) => p.work_item_id === w.id ||
  (!p.work_item_id && w.subcontractor_short && p.subcontractor_short === w.subcontractor_short && (!p.work_item_category || p.work_item_category === w.category)));

// Orijinal tutar + (gerekiyorsa) TL karşılığı
const MoneyTL = ({ amount, currency, tl, cls = "" }) => (
  <span className="amt-tl">
    <b className={`mono-small ${cls}`}>{money(amount, currency)}</b>
    {currency && currency !== "TRY" && tl != null && <small className="tl-eq">≈ {money(tl, "TRY")}</small>}
  </span>
);

function WiForm({ value, onChange, categories, subs, onSubmit, onCancel, testPrefix }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const [customCat, setCustomCat] = useState(false);
  const useCustom = customCat || (!!value.category && !categories.includes(value.category));
  return (
    <form className="inline-form wi-form-labeled" onSubmit={onSubmit} data-testid={`${testPrefix}-form`}>
      <label className="wi-fld">
        <span>Kategori</span>
        {useCustom ? (
          <input required placeholder="Yeni kategori adı" value={value.category} onChange={set("category")} data-testid={`${testPrefix}-category-input`} autoFocus />
        ) : (
          <select required value={value.category || ""} data-testid={`${testPrefix}-category-input`}
            onChange={(e) => { if (e.target.value === "__new__") { setCustomCat(true); onChange({ ...value, category: "" }); } else onChange({ ...value, category: e.target.value }); }}>
            <option value="">Kategori seçin…</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="__new__">+ Yeni kategori ekle…</option>
          </select>
        )}
      </label>
      <label className="wi-fld">
        <span>Alt Yüklenici <em className="fld-hint">(boşsa şirketimizin işi)</em></span>
        <input list={`${testPrefix}-subs`} placeholder="Alt yüklenici (opsiyonel)" value={value.subcontractor_short || ""} onChange={set("subcontractor_short")} data-testid={`${testPrefix}-sub-input`} />
        <datalist id={`${testPrefix}-subs`}>{subs.map((s) => <option key={s.id} value={s.short_name}>{s.name}</option>)}</datalist>
      </label>
      <label className="wi-fld">
        <span>İşveren Bedeli (gelir)</span>
        <div className="amt-pair">
          <input type="number" step="0.01" placeholder="0" value={value.employer_amount} onChange={set("employer_amount")} data-testid={`${testPrefix}-employer-input`} />
          <select value={value.employer_currency} onChange={set("employer_currency")} data-testid={`${testPrefix}-employer-cur`}>{CURS.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
      </label>
      <label className="wi-fld">
        <span>Alt Yüklenici Maliyeti</span>
        <div className="amt-pair">
          <input type="number" step="0.01" placeholder="0" value={value.agreed_amount} onChange={set("agreed_amount")} data-testid={`${testPrefix}-agreed-input`} />
          <select value={value.agreed_currency} onChange={set("agreed_currency")} data-testid={`${testPrefix}-agreed-cur`}>{CURS.map((c) => <option key={c}>{c}</option>)}</select>
        </div>
      </label>
      <label className="wi-fld">
        <span>Ödenen</span>
        <input type="number" step="0.01" placeholder="0" value={value.paid_amount} onChange={set("paid_amount")} data-testid={`${testPrefix}-paid-input`} />
      </label>
      <label className="wi-fld">
        <span>Durum</span>
        <select value={value.status || ""} onChange={set("status")} data-testid={`${testPrefix}-status-select`}>
          <option value="">Seçin…</option><option>Bekliyor</option><option>Devam</option><option>Tamamlandı</option>
        </select>
      </label>
      <div className="row-actions wi-fld-actions">
        <button className="new-btn" data-testid={`${testPrefix}-save`}><Save size={14} /> Kaydet</button>
        {onCancel && <button type="button" className="ghost-btn" onClick={onCancel} data-testid={`${testPrefix}-cancel`}><X size={14} /></button>}
      </div>
    </form>
  );
}

export default function WorkItemsPanel({ prj, reload, onQuickPay }) {
  const { can } = useAuth();
  const editable = can("admin", "project_manager");
  const itemized = prj.deal_type === "itemized";
  const cur = prj.currency;
  const emptyForm = () => ({ category: "", subcontractor_short: "", employer_amount: "", employer_currency: cur, agreed_amount: "", agreed_currency: cur, paid_amount: "", status: "" });

  const [subs, setSubs] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [priceTarget, setPriceTarget] = useState(null);

  const loadCats = () => api.get("/work-items/categories").then((r) => setCats(r.data.all));
  useEffect(() => { api.get("/subcontractors").then((r) => setSubs(r.data)); loadCats(); }, []);

  const num = (v) => Number(v) || 0;
  const payload = (f) => ({
    project_id: prj.id, category: f.category,
    subcontractor_short: f.subcontractor_short || null, status: f.status || null,
    agreed_amount: num(f.agreed_amount), agreed_currency: f.agreed_currency || cur,
    employer_amount: num(f.employer_amount), employer_currency: f.employer_currency || cur,
    paid_amount: num(f.paid_amount),
  });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/work-items", payload(form));
      toast.success("İş kalemi eklendi"); setForm(emptyForm()); reload();
      if (!cats.includes(form.category)) setCats([...cats, form.category]);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/work-items/${editing.id}`, payload(editing));
      toast.success("İş kalemi güncellendi"); setEditing(null); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (w) => { if (!window.confirm(`${w.category} silinsin mi?`)) return; await api.delete(`/work-items/${w.id}`); toast.success("İş kalemi silindi"); reload(); };
  const startEdit = (w) => setEditing({ ...w, employer_amount: w.employer_amount ?? "", agreed_amount: w.agreed_amount ?? "", paid_amount: w.paid_amount ?? "", employer_currency: w.employer_currency || cur, agreed_currency: w.agreed_currency || cur });

  const headCols = ["KATEGORİ", "ALT YÜKLENİCİ", "İŞVEREN BEDELİ", "ALT YÜKLENİCİ MALİYETİ", "ÖDENEN", "ALT YÜK. KALAN", "KÂR/ZARAR", ""];

  const totalRemaining = prj.wi_sub_remaining_try;

  return (
    <section className="panel" data-testid="work-items-panel">
      <div className="panel-head">
        <div><span className="eyebrow">İŞ KALEMLERİ</span><h2>Kapsam & Alt Yükleniciler</h2></div>
        <small className="rate-note">{itemized ? "İş kalemi bazlı: gelir kalemlerin işveren bedelleri toplamı" : "Kırılımsız: proje geliri proje bedelinden; işveren bedeli kalem bazında bilgi amaçlı"}</small>
      </div>
      {totalRemaining != null && (
        <div className="wi-remaining-box" data-testid="wi-total-remaining">
          <div><span className="eyebrow">ALT YÜKLENİCİYE KALAN TOPLAM ÖDEME</span><strong className={totalRemaining > 0 ? "profit-neg" : "profit-pos"}>{money(totalRemaining, "TRY")}</strong></div>
          <small>Tüm iş kalemleri · maliyet − ödenen (TL karşılığı)</small>
        </div>
      )}
      {prj.work_items.some((w) => w.subcontractor_short) && (
        <div className="wi-remaining-cards" data-testid="wi-remaining-cards">
          {prj.work_items.filter((w) => w.subcontractor_short).map((w, i) => (
            <div className="wi-rem-card" key={w.id} data-testid={`wi-rem-card-${i}`}>
              <div className="wi-rem-card-head"><b>{w.category}</b><span>{w.subcontractor_short}</span></div>
              <div className="wi-rem-card-body">
                <div><small>Maliyet</small><b>{money(w.agreed_amount, w.agreed_currency || cur)}</b></div>
                <div><small>Ödenen</small><b>{money(w.paid_amount, w.agreed_currency || cur)}</b></div>
                <div><small>Kalan</small><b className={(w.remaining_to_sub || 0) > 0 ? "profit-neg" : "profit-pos"}>{money(w.remaining_to_sub, w.agreed_currency || cur)}</b></div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="project-table">
        <div className="table-row table-head wi-row-it2">{headCols.map((h, i) => <span key={i}>{h}</span>)}</div>
        {prj.work_items.map((w, i) => {
          const pays = linked(w, prj.payments);
          const paidFromPays = pays.filter((p) => p.status === "Ödendi").reduce((s, p) => s + (p.amount || 0), 0);
          const isOpen = open === w.id;
          const profit = w.profit_try;
          return (
            <div key={w.id} className={`wi-block ${isOpen ? "open" : ""}`}>
              <div className="table-row wi-row-it2 clickable" data-testid={`wi-row-${i}`} onClick={() => setOpen(isOpen ? null : w.id)}>
                <b className="wi-cat">{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {w.category}
                  {!w.subcontractor_short && <em className="status mini status-green">Kendi işimiz</em>}
                </b>
                <span>{w.subcontractor_short || "—"}</span>
                {w.employer_amount ? <MoneyTL amount={w.employer_amount} currency={w.employer_currency || cur} tl={w.employer_try} /> : <span className="mono-small muted-cell">—</span>}
                {w.subcontractor_short ? <MoneyTL amount={w.agreed_amount} currency={w.agreed_currency || cur} tl={w.cost_try} /> : <span className="mono-small muted-cell">—</span>}
                <span className="mono-small">{money(w.paid_amount, w.agreed_currency || cur)}{pays.length ? <small className="sub-note"> · {pays.length}</small> : null}</span>
                {w.subcontractor_short ? <MoneyTL amount={w.remaining_to_sub} currency={w.agreed_currency || cur} tl={w.sub_remaining_try} cls={w.sub_remaining_try > 0 ? "profit-neg" : "profit-pos"} /> : <span className="mono-small muted-cell">—</span>}
                <b className={`mono-small ${profit != null ? (profit >= 0 ? "profit-pos" : "profit-neg") : ""}`} data-testid={`wi-profit-${i}`}>{profit != null ? money(profit, "TRY") : "—"}</b>
                <div className="row-actions" onClick={(e) => e.stopPropagation()}>
                  {editable && <button className="row-action" onClick={() => { startEdit(w); setOpen(w.id); }} data-testid={`edit-wi-${i}`}><Pencil size={13} /></button>}
                  {editable && <button className="row-action danger" onClick={() => remove(w)} data-testid={`delete-wi-${i}`}><Trash2 size={13} /></button>}
                </div>
              </div>
              {isOpen && (
                <div className="wi-detail" data-testid={`wi-detail-${i}`}>
                  {editing?.id === w.id && <WiForm value={editing} onChange={setEditing} categories={cats} subs={subs} onSubmit={save} onCancel={() => setEditing(null)} testPrefix={`edit-wi-${i}`} />}
                  <div className="wi-detail-head">
                    <span className="eyebrow">{w.subcontractor_short || "ALT YÜKLENİCİ"} · ÖDEME HAREKETLERİ</span>
                    <b className="mono-small" data-testid={`wi-paid-sum-${i}`}>Ödemelerden: {money(paidFromPays, cur)} · Durum: {w.status || "—"}</b>
                    {editable && <button className="view-all" onClick={() => setPriceTarget({ kind: "work-item", id: w.id, name: w.category, current: w.agreed_amount, currency: w.agreed_currency || cur })} data-testid={`wi-price-adjust-${i}`}><TrendingUp size={13} /> Fiyat Farkı</button>}
                    {can("admin", "project_manager", "finance") && <button className="view-all" onClick={() => onQuickPay({ project_id: prj.id, work_item_id: w.id, subcontractor_short: w.subcontractor_short, direction: "outgoing" })} data-testid={`wi-quick-pay-${i}`}><Plus size={13} /> Ödeme kaydet</button>}
                  </div>
                  {w.price_history?.length > 0 && <PriceHistory history={w.price_history} currency={w.agreed_currency || cur} testid={`wi-price-history-${i}`} />}
                  {pays.length ? pays.map((p) => (
                    <div className="mini-pay" key={p.id}>
                      <span>{p.stage}</span><span>{p.date ? p.date.slice(0, 10) : "—"}</span><span>{p.due_date ? `vade ${p.due_date.slice(0, 10)}` : ""}</span>
                      <b className="mono-small">{money(p.amount, p.currency)}</b>
                      <em className={`status ${p.status === "Bekliyor" ? "status-amber" : p.status === "Planlanan" ? "status-blue" : "status-green"}`}>{p.status}</em>
                      <small>{p.description}</small>
                    </div>
                  )) : <div className="empty-hint small">Bu kaleme bağlı ödeme kaydı yok.</div>}
                </div>
              )}
            </div>
          );
        })}
        {!prj.work_items.length && <div className="empty-hint">Bu proje için iş kalemi girilmemiş.</div>}
      </div>
      {editable && <WiForm value={form} onChange={setForm} categories={cats} subs={subs} onSubmit={add} testPrefix="add-wi" />}
      {priceTarget && <PriceAdjustModal target={priceTarget} close={() => setPriceTarget(null)} onSaved={reload} />}
    </section>
  );
}
